import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "./AuthContext";
import { useLanguage } from "./LanguageContext";
import { useCurrency } from "./CurrencyContext";

const API = process.env.REACT_APP_API_URL || "";
const PENDING_PAYMENT_KEY = "sm_pending_payment_id";

const paymentEndpointForCurrency = (currency) =>
  currency === "RUB" ? "/api/payments/create" : "/api/payments/trybit/create";

const writePendingPayment = (provider, paymentId) => {
  localStorage.setItem(
    PENDING_PAYMENT_KEY,
    JSON.stringify({ provider, payment_id: String(paymentId) })
  );
};

const SubscriptionModal = ({ onClose }) => {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const { currency, setCurrency, getPrice, currencies } = useCurrency();

  const [billing, setBilling] = useState("monthly");
  const [selectedTier, setSelectedTier] = useState("premium");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const currencyRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (currencyRef.current && !currencyRef.current.contains(e.target))
        setCurrencyOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectedPlanId = `${selectedTier}_${billing}`;
  const allPlans = t("subscription.plans");
  const tierPlans = {
    pro:     { monthly: allPlans.find((p) => p.id === "pro_monthly"),     yearly: allPlans.find((p) => p.id === "pro_yearly")     },
    premium: { monthly: allPlans.find((p) => p.id === "premium_monthly"), yearly: allPlans.find((p) => p.id === "premium_yearly") },
  };
  const activePlan = tierPlans[selectedTier]?.[billing];
  const priceInfo  = getPrice(selectedPlanId);

  const handleSubscribe = async () => {
    if (!user) { window.location.href = "/log-in"; return; }
    setLoading(true);
    setError("");
    try {
      const endpoint = paymentEndpointForCurrency(currency);
      const res = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          plan: selectedPlanId,
          user_id: user.id || user.user_id,
          email: user.email,
          return_url: `${window.location.origin}/premium`,
          currency,
          display_price: priceInfo.display,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Payment error");
      writePendingPayment(data.provider || (currency === "RUB" ? "yookassa" : "trybit"), data.payment_id);
      window.location.href = data.confirmation_url;
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  };

  return (
    <Backdrop
      as={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <Modal
        as={motion.div}
        initial={{ opacity: 0, y: 40, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 40, scale: 0.96 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
      >
        <CloseBtn onClick={onClose}>✕</CloseBtn>

        <Badge>{t("modal.badge")}</Badge>
        <Title>{t("modal.title")}</Title>
        <Subtitle>{t("modal.subtitle")}</Subtitle>

        <Perks>
          {t("modal.perks").map((p) => (
            <Perk key={p}><Check>✓</Check> {p}</Perk>
          ))}
        </Perks>

        {/* Billing + Currency row */}
        <ControlsRow>
          <BillingToggle>
            <BillingBtn active={billing === "monthly"} onClick={() => setBilling("monthly")}>
              {t("modal.billingMonthly")}
            </BillingBtn>
            <BillingBtn active={billing === "yearly"} onClick={() => setBilling("yearly")}>
              {t("modal.billingYearly")}
              <SavePill>{t("modal.billingSaveLabel")}</SavePill>
            </BillingBtn>
          </BillingToggle>

          <CurrencyPickerWrap ref={currencyRef}>
            <CurrencyBtn onClick={() => setCurrencyOpen((o) => !o)}>
              {currency}
              <CurrencyCaret open={currencyOpen}>▾</CurrencyCaret>
            </CurrencyBtn>
            <AnimatePresence>
              {currencyOpen && (
                <CurrencyDropdown
                  as={motion.div}
                  initial={{ opacity: 0, y: -6, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -6, scale: 0.97 }}
                  transition={{ duration: 0.15 }}
                >
                  {currencies.map((c) => (
                    <CurrencyOption
                      key={c.code}
                      active={c.code === currency}
                      onClick={() => { setCurrency(c.code); setCurrencyOpen(false); }}
                    >
                      <span style={{ fontWeight: 700, minWidth: 34 }}>{c.code}</span>
                      <span style={{ opacity: 0.5, fontSize: "0.72rem" }}>
                        {c.label.split(" — ")[1] || c.label}
                      </span>
                    </CurrencyOption>
                  ))}
                </CurrencyDropdown>
              )}
            </AnimatePresence>
          </CurrencyPickerWrap>
        </ControlsRow>

        {/* Plan cards */}
        <PlansRow>
          {["pro", "premium"].map((tier) => {
            const plan = tierPlans[tier]?.[billing];
            if (!plan) return null;
            const price = getPrice(plan.id);
            const isSelected = selectedTier === tier;
            return (
              <PlanCard
                key={tier}
                active={isSelected ? 1 : 0}
                onClick={() => setSelectedTier(tier)}
              >
                {plan.note && <PlanBadge>{plan.note}</PlanBadge>}
                <PlanLabel>{plan.label}</PlanLabel>
                <PlanCredits>{plan.credits.toLocaleString()} cr</PlanCredits>
                <PlanPriceRow>
                  <PlanPrice>{price.amount}</PlanPrice>
                  <PlanSymbol>{price.symbol}</PlanSymbol>
                </PlanPriceRow>
                <PlanPer>{plan.per}</PlanPer>
              </PlanCard>
            );
          })}
        </PlansRow>

        <AnimatePresence>
          {error && (
            <ErrorBox
              as={motion.div}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              {error}
            </ErrorBox>
          )}
        </AnimatePresence>

        <PayBtn onClick={handleSubscribe} disabled={loading}>
          {loading
            ? t("modal.payBtnLoading")
            : `${activePlan?.label || ""} — ${priceInfo.display} ${activePlan?.per || ""} →`}
        </PayBtn>

        <Legal>{t("modal.legal")}</Legal>
      </Modal>
    </Backdrop>
  );
};

/* ── Styled ── */
const Backdrop = styled.div`
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.75); backdrop-filter: blur(6px);
  z-index: 2000; display: flex; align-items: center; justify-content: center; padding: 1rem;
`;

const Modal = styled.div`
  position: relative; background: #fff;
  border: 1px solid rgba(76,175,80,0.2); border-radius: 16px;
  padding: 2.5rem 2rem 2rem; max-width: 500px; width: 100%;
  font-family: 'Montserrat', sans-serif; color: #111;
  box-shadow: 0 16px 48px rgba(0,0,0,0.15);
  max-height: 90vh; overflow-y: auto;
`;

const CloseBtn = styled.button`
  position: absolute; top: 1rem; right: 1rem;
  background: none; border: none; color: rgba(0,0,0,0.3);
  font-size: 1rem; cursor: pointer; transition: color 0.2s;
  &:hover { color: #111; }
`;

const Badge = styled.div`
  display: inline-block; font-size: 0.7rem; font-weight: 700;
  letter-spacing: 0.1em; text-transform: uppercase;
  background: rgba(76,175,80,0.15); border: 1px solid rgba(76,175,80,0.3);
  color: #4CAF50; padding: 0.3rem 0.8rem; border-radius: 20px; margin-bottom: 1rem;
`;

const Title = styled.h2`margin: 0 0 0.4rem; font-size: 1.4rem; font-weight: 800; line-height: 1.2;`;
const Subtitle = styled.p`margin: 0 0 1.25rem; font-size: 0.82rem; color: rgba(0,0,0,0.45);`;

const Perks = styled.ul`list-style: none; padding: 0; margin: 0 0 1.25rem; display: flex; flex-direction: column; gap: 0.4rem;`;
const Perk = styled.li`font-size: 0.85rem; color: rgba(0,0,0,0.7); display: flex; align-items: center; gap: 0.5rem;`;
const Check = styled.span`color: #4CAF50; font-weight: 700;`;

const ControlsRow = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  gap: 0.75rem; margin-bottom: 1rem; flex-wrap: wrap;
`;

const BillingToggle = styled.div`
  display: inline-flex; background: #f0f7f0;
  border: 1px solid rgba(76,175,80,0.2); border-radius: 7px; padding: 3px; gap: 2px;
`;

const BillingBtn = styled.button`
  display: flex; align-items: center; gap: 0.4rem;
  padding: 0.35rem 0.8rem; border: none; border-radius: 5px; font-family: inherit;
  font-size: 0.72rem; font-weight: 700; cursor: pointer;
  transition: background 0.15s, color 0.15s;
  background: ${({ active }) => active ? "#4CAF50" : "transparent"};
  color: ${({ active }) => active ? "#000" : "rgba(0,0,0,0.45)"};
`;

const SavePill = styled.span`
  font-size: 0.55rem; font-weight: 800; text-transform: uppercase;
  background: rgba(0,0,0,0.12); border-radius: 8px; padding: 0.08rem 0.35rem;
`;

const CurrencyPickerWrap = styled.div`position: relative;`;

const CurrencyBtn = styled.button`
  display: flex; align-items: center; gap: 0.3rem;
  background: rgba(76,175,80,0.08); border: 1px solid rgba(76,175,80,0.25);
  border-radius: 5px; padding: 0.35rem 0.65rem;
  font-family: inherit; font-size: 0.72rem; font-weight: 700;
  color: #4CAF50; cursor: pointer; transition: background 0.15s;
  &:hover { background: rgba(76,175,80,0.14); }
`;

const CurrencyCaret = styled.span`
  font-size: 0.55rem; transition: transform 0.18s;
  transform: ${({ open }) => open ? "rotate(180deg)" : "none"};
`;

const CurrencyDropdown = styled.div`
  position: absolute; top: calc(100% + 5px); right: 0;
  min-width: 200px; max-height: 260px; overflow-y: auto;
  background: #fff; border: 1px solid rgba(0,0,0,0.08);
  border-radius: 10px; box-shadow: 0 8px 28px rgba(0,0,0,0.15); z-index: 300;
`;

const CurrencyOption = styled.div`
  display: flex; align-items: center; gap: 0.5rem;
  padding: 0.55rem 0.9rem; cursor: pointer;
  background: ${({ active }) => active ? "rgba(76,175,80,0.07)" : "transparent"};
  font-size: 0.8rem; transition: background 0.1s;
  &:hover { background: rgba(76,175,80,0.1); }
`;

const PlansRow = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; margin-bottom: 1.25rem;
`;

const PlanCard = styled.div`
  position: relative;
  border: 2px solid ${({ active }) => active ? "#4CAF50" : "rgba(0,0,0,0.1)"};
  border-radius: 10px; padding: 1.25rem 1rem 1rem; cursor: pointer;
  background: ${({ active }) => active ? "rgba(76,175,80,0.07)" : "#f8f8f8"};
  transition: border-color 0.2s, background 0.2s; text-align: center;
  &:hover { border-color: ${({ active }) => active ? "#4CAF50" : "rgba(76,175,80,0.4)"}; }
`;

const PlanBadge = styled.div`
  position: absolute; top: -10px; left: 50%; transform: translateX(-50%);
  background: #4CAF50; color: #000; font-size: 0.58rem; font-weight: 800;
  text-transform: uppercase; padding: 0.18rem 0.55rem; border-radius: 20px; white-space: nowrap;
`;

const PlanLabel = styled.div`font-size: 0.68rem; font-weight: 700; letter-spacing: 0.15em; text-transform: uppercase; color: rgba(0,0,0,0.4); margin-bottom: 0.3rem;`;
const PlanCredits = styled.div`font-size: 0.68rem; color: #4CAF50; font-weight: 700; margin-bottom: 0.5rem;`;
const PlanPriceRow = styled.div`display: flex; align-items: baseline; justify-content: center; gap: 0.15rem;`;
const PlanPrice = styled.div`font-size: 1.8rem; font-weight: 900; line-height: 1; color: #111;`;
const PlanSymbol = styled.div`font-size: 0.95rem; font-weight: 700; color: rgba(0,0,0,0.55);`;
const PlanPer = styled.div`font-size: 0.68rem; color: rgba(0,0,0,0.35); margin-top: 0.2rem;`;

const ErrorBox = styled.div`
  background: rgba(220,50,50,0.12); border: 1px solid rgba(220,50,50,0.35);
  border-radius: 6px; padding: 0.55rem 0.9rem;
  font-size: 0.8rem; color: #ff6b6b; margin-bottom: 0.9rem;
`;

const PayBtn = styled.button`
  width: 100%; padding: 1rem; background: #4CAF50; color: #000;
  font-family: inherit; font-size: 0.92rem; font-weight: 700;
  border: 2px solid #4CAF50; border-radius: 8px; cursor: pointer;
  box-shadow: 0 0 24px rgba(76,175,80,0.35);
  transition: background 0.2s, color 0.2s, box-shadow 0.2s;
  &:hover:not(:disabled) { background: transparent; color: #4CAF50; box-shadow: 0 0 36px rgba(76,175,80,0.5); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const Legal = styled.p`
  margin: 0.7rem 0 0; text-align: center;
  font-size: 0.7rem; color: rgba(0,0,0,0.35);
`;

export default SubscriptionModal;
